/**
 * chartModel.js - the progress chart's presentation model.
 *
 * PURE. Takes the payload from GET /api/engine/children/:childId/chart and
 * returns everything the UI draws. No React, no DOM, no fetch - so it is
 * testable with `node --test`, which is where the rules below are actually
 * checked rather than eyeballed.
 *
 * ---------------------------------------------------------------------------
 * IT DOES NOT SCORE ANYTHING
 *
 * Every colour here came from the engine. scoring.js decides what a box is
 * worth, scoreLevel() decides that a level is its lowest scoreable box, and
 * this file must never develop an opinion about either - a second copy of the
 * colour rules would drift from the first and the chart is the whole point of
 * the framework. What this file adds is presentation, and only presentation:
 *
 *   1. THE LATCH. It paints `bestColor` - the high-water mark - rather than
 *      `color`, which is the last ten plays and is allowed to fall. Reasoning
 *      is in database/migrate_box_score_best_color.sql; the short version is
 *      that the bands are two plays wide, so a genuinely Green box reads
 *      8-or-fewer in about a quarter of its windows, and showing a parent that
 *      flicker is a delayed buzzer on the one screen they use to decide
 *      whether any of this is working.
 *
 *      The engine is untouched and still sees the fall. It must: pacing and
 *      the support ladder read `color`, and a latch inside the engine would
 *      keep advancing a child whose performance had genuinely decayed.
 *
 *   2. THE FOUR NON-COLOURS, told apart. Grey meant two different things and
 *      they are not the same thing at all:
 *        not_started    nothing played here yet
 *        in_progress    played, fewer than ten counted goes, no colour yet
 *        observational  21 boxes nothing about one utterance can score
 *        not_assessable no game this child has played declares the capability
 *      The last is the one a parent can act on, by trying a different game.
 *
 *   3. THE HONEST DENOMINATOR. Progress is green over SCOREABLE, never over
 *      208. Dividing by 208 caps every child at 90% for ever because 21 boxes
 *      can never be Green, and a progress bar that cannot fill is worse than
 *      no progress bar. Not-assessable boxes come out of the denominator too,
 *      for the same reason - and the denominator GROWS when the child plays a
 *      game that can measure them, which is honest and is exactly the message
 *      the chart gives them.
 * ---------------------------------------------------------------------------
 */

import {
  PHASES,
  PHASE_NUMBERS,
  COLORS,
  COLOR_ORDER,
  SCORING_WINDOW,
  getBoxesForLevel,
} from '../engine/framework.js';
import { boxLanguage } from './parentLanguage.js';

/** Non-colour states, in the vocabulary the UI uses. */
export const STATES = Object.freeze({
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  OBSERVATIONAL: 'observational',
  NOT_ASSESSABLE: 'not_assessable',
});

/**
 * How far apart two colours are on the five-band scale.
 * Non-colours have no position, so any comparison involving one is null.
 * @returns {number|null}
 */
export function bandGap(from, to) {
  const a = COLOR_ORDER.indexOf(from);
  const b = COLOR_ORDER.indexOf(to);
  if (a === -1 || b === -1) return null;
  return a - b;
}

/**
 * How far the recent window has slipped below the high-water mark, before it
 * is worth saying anything to a parent.
 *
 * ONE BAND IS NOISE. At a true first-attempt rate of 0.90, a ten-play window
 * reads 8-or-fewer about 26% of the time - one band down, with nothing having
 * changed about the child. Two bands down across a FULL window is a signal.
 * Marked as an invitation to play, never as a loss. See parentLanguage.js.
 */
export const DECAY_BANDS = 2;

/* =========================================================================
 * ONE BOX
 * ========================================================================= */

/**
 * @param {object} box a chart.levels[].boxes[] entry from the API
 * @returns {object} the view model for one square
 */
export function modelBox(box) {
  const language = boxLanguage(box.phase, box.code, box.name);

  // Permanent, framework-level, and it outranks everything else: a box no
  // microphone can score is never "not started" and never a failure.
  if (box.state === 'observational') {
    return {
      boxId: box.boxId,
      phase: box.phase,
      level: box.level,
      code: box.code,
      name: box.name,
      ...language,
      state: STATES.OBSERVATIONAL,
      color: null,
      scoreable: false,
      plays: 0,
      windowSize: SCORING_WINDOW,
      successes: 0,
      readyForAnotherGo: false,
      why: box.observationalReason || '',
    };
  }

  // Nothing the child has played can measure this. Different from not started,
  // and they cannot fix it by playing more of the same game.
  if (box.notAssessable) {
    return {
      boxId: box.boxId,
      phase: box.phase,
      level: box.level,
      code: box.code,
      name: box.name,
      ...language,
      state: STATES.NOT_ASSESSABLE,
      color: null,
      scoreable: false,
      plays: 0,
      windowSize: SCORING_WINDOW,
      successes: 0,
      readyForAnotherGo: false,
      requires: box.requires || null,
    };
  }

  // What we paint. `bestColor` is the latch; it falls back to the live colour
  // when the migration has not been applied, which is honest - the chart then
  // simply shows current performance and the marker below never fires.
  //
  // Taken as the HIGHER of the two by rank, not simply as bestColor. A row last
  // written by a server that predates the column carries a best that is behind
  // the current reading, and painting that would do the exact thing this whole
  // mechanism exists to prevent: show a child going backwards. Self-healing -
  // the next attempt on the box writes the real high-water mark.
  const current = box.color;
  const latched = box.bestColor || box.color;
  const shown = (bandGap(latched, current) || 0) >= 0 ? latched : current;
  const plays = box.plays || 0;

  // Established means ten counted plays. Before that there is no colour, only
  // a partial fill - section 7.6.
  const established = Boolean(box.established);
  const hasColor = established && COLOR_ORDER.includes(shown);

  const slipped = bandGap(shown, current);

  return {
    boxId: box.boxId,
    phase: box.phase,
    level: box.level,
    code: box.code,
    name: box.name,
    ...language,
    state: hasColor
      ? shown
      : (plays > 0 ? STATES.IN_PROGRESS : STATES.NOT_STARTED),
    color: hasColor ? shown : null,
    currentColor: current,
    bestAchievedAt: box.bestAchievedAt || null,
    scoreable: true,
    established,
    plays,
    windowSize: box.windowSize || SCORING_WINDOW,
    successes: box.successes || 0,
    // How full the ten-go window is, for the partial fill section 7.6 permits.
    fillFraction: box.fillFraction !== undefined
      ? box.fillFraction
      : plays / SCORING_WINDOW,
    // Only ever true for a box that HAS a high-water mark and has since
    // slipped two full bands over a complete window. Never shown as a loss.
    readyForAnotherGo: Boolean(
      box.latchAvailable
      && established
      && slipped !== null
      && slipped >= DECAY_BANDS
    ),
  };
}

/* =========================================================================
 * ONE LEVEL
 * ========================================================================= */

/**
 * The level's colour is the LOWEST of its scoreable boxes - section 7.5,
 * applied to what is painted. Observational and not-assessable boxes are
 * excluded, exactly as scoring.scoreLevel() excludes them: they are absent
 * measurements, not poor ones, and ranking them would hold every level
 * containing one at grey for ever.
 */
export function modelLevel(level, generalizationByKey = {}) {
  const boxes = (level.boxes || []).map(modelBox);
  const scoreable = boxes.filter((b) => b.scoreable);

  const coloured = scoreable.filter((b) => b.color);
  const anyPlayed = scoreable.some((b) => b.plays > 0);

  let color = null;
  let state = STATES.NOT_STARTED;

  if (!scoreable.length) {
    // Every box here is observational or unmeasurable by the child's games.
    state = boxes.some((b) => b.state === STATES.NOT_ASSESSABLE)
      ? STATES.NOT_ASSESSABLE
      : STATES.OBSERVATIONAL;
  } else if (coloured.length === scoreable.length) {
    color = coloured.reduce(
      (low, b) => (COLORS[b.color].rank < COLORS[low].rank ? b.color : low),
      coloured[0].color
    );
    state = color;
  } else {
    state = anyPlayed ? STATES.IN_PROGRESS : STATES.NOT_STARTED;
  }

  const key = `${level.phase}.${level.level}`;
  const general = generalizationByKey[key] || null;

  return {
    phase: level.phase,
    level: level.level,
    boxes,
    color,
    state,
    mastered: color === 'green',
    scoreableBoxes: scoreable.length,
    totalBoxes: boxes.length,
    greenBoxes: scoreable.filter((b) => b.color === 'green').length,
    observationalBoxes: boxes.filter((b) => b.state === STATES.OBSERVATIONAL).length,
    notAssessableBoxes: boxes.filter((b) => b.state === STATES.NOT_ASSESSABLE).length,
    readyForAnotherGo: boxes.some((b) => b.readyForAnotherGo),
    // Section 9.3. Green in two or more different games means something Green
    // in one does not, and that distinction is the point of the framework.
    generalization: general
      ? {
        verified: Boolean(general.verified),
        greenGames: general.greenGames || [],
        remaining: general.remaining || 0,
      }
      : null,
  };
}

/* =========================================================================
 * THE WHOLE CHART
 * ========================================================================= */

/**
 * @param {object} payload the /chart response
 * @returns {object} phases, levels, boxes and the headline numbers
 */
export function buildChartModel(payload) {
  const chart = (payload && payload.chart) || {};
  const levelsIn = chart.levels || [];

  // Section 9.3, indexed for lookup. `verified` and `pending` are separate
  // lists in the payload; the chart wants one answer per level.
  const generalizationByKey = {};
  const gen = (payload && payload.generalization) || {};
  for (const v of gen.verified || []) {
    generalizationByKey[`${v.phase}.${v.level}`] = { ...v, verified: true };
  }
  for (const v of gen.pending || []) {
    generalizationByKey[`${v.phase}.${v.level}`] = { ...v, verified: false };
  }

  const levels = levelsIn.map((l) => modelLevel(l, generalizationByKey));

  // ---- phases ------------------------------------------------------------
  // Structure comes from the framework, never from the payload and never from
  // a constant: boxes per row VARY inside a phase (phase 7 carries component E
  // on levels 3-5 only, phase 8 on levels 3-6), which is exactly what the old
  // mockup got wrong when it drew 212 boxes and labelled them 208.
  const phases = PHASE_NUMBERS.map((n) => {
    const spec = PHASES[n];
    const rows = levels.filter((l) => l.phase === n);
    const boxes = rows.flatMap((l) => l.boxes);
    const scoreable = boxes.filter((b) => b.scoreable);
    const green = scoreable.filter((b) => b.color === 'green').length;

    return {
      phase: n,
      name: spec.parentName,
      clinicalName: spec.name,
      description: spec.parentDescription,
      goal: spec.goal,
      levels: rows,
      totalBoxes: boxes.length,
      scoreableBoxes: scoreable.length,
      greenBoxes: green,
      masteredLevels: rows.filter((l) => l.mastered).length,
      totalLevels: rows.length,
      started: scoreable.some((b) => b.plays > 0),
      complete: scoreable.length > 0 && green === scoreable.length,
      readyForAnotherGo: rows.some((l) => l.readyForAnotherGo),
      notAssessableBoxes: boxes.filter((b) => b.state === STATES.NOT_ASSESSABLE).length,
      observationalBoxes: boxes.filter((b) => b.state === STATES.OBSERVATIONAL).length,
      generalizedLevels: rows.filter((l) => l.generalization && l.generalization.verified).length,
    };
  });

  // ---- the headline ------------------------------------------------------
  const allBoxes = levels.flatMap((l) => l.boxes);
  const scoreable = allBoxes.filter((b) => b.scoreable);
  const green = scoreable.filter((b) => b.color === 'green').length;

  const position = (payload && payload.position) || {};
  const furthest = position.furthest || position;
  const currentPhase = furthest.phase ? PHASES[furthest.phase] : null;

  return {
    phases,
    levels,
    position: {
      phase: furthest.phase || 1,
      level: furthest.level || 1,
      phaseName: currentPhase ? currentPhase.parentName : '',
      phaseDescription: currentPhase ? currentPhase.parentDescription : '',
    },
    summary: {
      // 208, from the framework, never typed out.
      totalBoxes: allBoxes.length,
      // The denominator. Never 208 - see the header.
      scoreableBoxes: scoreable.length,
      greenBoxes: green,
      percent: scoreable.length ? Math.round((green / scoreable.length) * 100) : 0,
      startedBoxes: scoreable.filter((b) => b.plays > 0).length,
      observationalBoxes: allBoxes.filter((b) => b.state === STATES.OBSERVATIONAL).length,
      notAssessableBoxes: allBoxes.filter((b) => b.state === STATES.NOT_ASSESSABLE).length,
      readyForAnotherGo: allBoxes.filter((b) => b.readyForAnotherGo).length,
      masteredLevels: levels.filter((l) => l.mastered).length,
      totalLevels: levels.length,
      generalizedLevels: levels.filter((l) => l.generalization && l.generalization.verified).length,
      totalPhases: phases.length,
    },
    // What the child's games can and cannot measure, and what would change it.
    assessability: (payload && payload.assessability) || null,
    // False when the migration has not been applied: the chart is then showing
    // current performance and the marker never fires. Surfaced so the UI can
    // stay quiet about a latch it does not have.
    latched: Boolean(payload && payload.latch && payload.latch.available),
    games: (payload && payload.games) || [],
    gameNames: (payload && payload.gameNames) || {},
  };
}

/**
 * The framework's own shape, for anything that needs to draw an empty chart
 * before the payload arrives. Boxes per row come from getBoxesForLevel(),
 * which is the only correct source: they are not constant within a phase.
 */
export function frameworkSkeleton() {
  return PHASE_NUMBERS.map((n) => ({
    phase: n,
    name: PHASES[n].parentName,
    levels: PHASES[n].levels.map((row) => ({
      phase: n,
      level: row.level,
      boxes: getBoxesForLevel(n, row.level).length,
    })),
  }));
}

export default { buildChartModel, modelLevel, modelBox, bandGap, frameworkSkeleton, STATES };
